import * as React from 'react'
import {
  Calendar,
  Download,
  Eye,
  FileType,
  FolderOpen,
  Image,
  Palette,
  Sparkles,
  Tag,
  Video,
  VideoOff,
} from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { formatDate } from '@/lib/utils'
import type { Asset } from '@/types'

interface Props {
  asset: Asset | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

function formatBytes(bytes: number | null): string {
  if (bytes == null) return '-'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function DetailRow({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ElementType
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="flex items-start gap-3 py-2">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </p>
        <div className="mt-0.5 text-sm break-all">{children}</div>
      </div>
    </div>
  )
}

const STATUS_LABELS: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'success' | 'outline' }> = {
  pending: { label: 'Pending', variant: 'secondary' },
  processing: { label: 'Processing', variant: 'outline' },
  completed: { label: 'Completed', variant: 'success' },
  failed: { label: 'Failed', variant: 'destructive' },
  skipped: { label: 'Skipped', variant: 'secondary' },
}

function VideoPreview({ src, title }: { src: string; title: string }) {
  const [error, setError] = React.useState(false)

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-10 gap-3 bg-muted/30">
        <VideoOff className="h-8 w-8 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Preview unavailable</p>
        <a
          href={src}
          download={title}
          className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted transition-colors"
        >
          <Download className="h-3.5 w-3.5" /> Download original
        </a>
      </div>
    )
  }

  return (
    <video
      src={src}
      className="w-full max-h-80"
      controls
      preload="metadata"
      onError={() => setError(true)}
    />
  )
}

export function AssetDetailModal({ asset, open, onOpenChange }: Props) {
  if (!asset) return null

  const isImage = asset.file_type === 'image'
  const fileUrl = `/uploads/${asset.file_path}`
  const statusInfo = STATUS_LABELS[asset.analysis_status] || STATUS_LABELS.pending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] p-0">
        <DialogHeader className="px-6 pt-6 pb-0">
          <div className="flex items-center gap-3">
            <DialogTitle className="text-lg truncate">
              {asset.original_filename || asset.filename}
            </DialogTitle>
            <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
          </div>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(90vh-80px)] px-6 pb-6">
          {/* Preview */}
          <div className="mb-4 rounded-lg border border-border bg-black overflow-hidden">
            {isImage ? (
              <img
                src={fileUrl}
                alt={asset.original_filename || asset.filename}
                className="w-full max-h-80 object-contain"
              />
            ) : (
              <VideoPreview src={fileUrl} title={asset.original_filename || asset.filename} />
            )}
          </div>

          {/* AI Description */}
          {asset.ai_description && (
            <div className="space-y-1 mb-4">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                AI Description
              </p>
              <p className="text-sm leading-relaxed rounded-md border border-border bg-muted/30 p-3">
                {asset.ai_description}
              </p>
            </div>
          )}

          {/* AI Tags */}
          {asset.ai_tags && asset.ai_tags.length > 0 && (
            <div className="space-y-1.5 mb-4">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Tags
              </p>
              <div className="flex flex-wrap gap-1.5">
                {asset.ai_tags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="text-xs">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <Separator className="my-3" />

          {/* File info */}
          <div className="grid grid-cols-2 gap-x-4">
            <DetailRow icon={isImage ? Image : Video} label="File Type">
              {asset.file_type} ({asset.mime_type || 'unknown'})
            </DetailRow>

            <DetailRow icon={FileType} label="File Size">
              {formatBytes(asset.file_size_bytes)}
            </DetailRow>

            {asset.scene_type && (
              <DetailRow icon={Eye} label="Scene Type">
                <span className="capitalize">{asset.scene_type}</span>
              </DetailRow>
            )}

            <DetailRow icon={Calendar} label="Uploaded">
              {formatDate(asset.created_at)}
            </DetailRow>
          </div>

          {/* Dominant Colors */}
          {asset.dominant_colors && asset.dominant_colors.length > 0 && (
            <>
              <Separator className="my-3" />
              <DetailRow icon={Palette} label="Dominant Colors">
                <div className="flex items-center gap-2 mt-1">
                  {asset.dominant_colors.map((color, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <div
                        className="h-5 w-5 rounded border border-border"
                        style={{ backgroundColor: color }}
                      />
                      <span className="text-xs font-mono text-muted-foreground">
                        {color}
                      </span>
                    </div>
                  ))}
                </div>
              </DetailRow>
            </>
          )}

          {/* Analysis Status detail */}
          <Separator className="my-3" />
          <DetailRow icon={Sparkles} label="Analysis Status">
            <Badge variant={statusInfo.variant} className="text-xs">
              {statusInfo.label}
            </Badge>
            {asset.analysis_status === 'skipped' && (
              <p className="text-xs text-muted-foreground mt-1">
                No AI API key configured. Placeholder metadata generated from filename.
              </p>
            )}
            {asset.analysis_status === 'failed' && (
              <p className="text-xs text-destructive mt-1">
                AI analysis failed. The asset is still usable for generation context.
              </p>
            )}
          </DetailRow>

          {/* File path */}
          <DetailRow icon={FolderOpen} label="Storage Path">
            <span className="font-mono text-xs text-muted-foreground">
              {asset.file_path}
            </span>
          </DetailRow>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
}
